/**
 * RecordViewerService - Business logic for grouping and organizing drafts
 * Supports two viewing modes:
 * 1. By Tournament - Groups drafts by tournament, then by team
 * 2. By Team - Groups drafts by team, then by tournament
 */

class RecordViewerService {
  /**
   * Group drafts by tournament, then by team
   * @param {Array} drafts - Array of draft objects
   * @returns {Array} Grouped structure: [{ tournament, teams: [{ team, drafts: [...] }] }]
   */
  static groupByTournament(drafts) {
    if (!drafts || drafts.length === 0) return [];

    // Create a map of tournaments -> teams -> drafts
    const tournamentMap = new Map();

    drafts.forEach(draft => {
      const tournamentName = draft.tournament || 'Uncategorized';
      const teamName = draft.teamName || 'Unnamed Team';

      // Get or create tournament entry
      if (!tournamentMap.has(tournamentName)) {
        tournamentMap.set(tournamentName, new Map());
      }

      const teamsMap = tournamentMap.get(tournamentName);

      // Get or create team entry
      if (!teamsMap.has(teamName)) {
        teamsMap.set(teamName, []);
      }

      // Add draft to team
      teamsMap.get(teamName).push(draft);
    });

    // Convert maps to arrays and sort
    const result = [];
    tournamentMap.forEach((teamsMap, tournamentName) => {
      const teams = [];
      teamsMap.forEach((drafts, teamName) => {
        teams.push({
          team: teamName,
          drafts: this.sortDraftsByDate(drafts)
        });
      });

      // Sort teams by the newest draft date in each team (newest team first)
      teams.sort((a, b) => {
        // Get the newest date from each team's drafts
        const latestDateA = a.drafts.length > 0 ? new Date(a.drafts[0].date || a.drafts[0].updatedAt || 0) : new Date(0);
        const latestDateB = b.drafts.length > 0 ? new Date(b.drafts[0].date || b.drafts[0].updatedAt || 0) : new Date(0);

        // Newest team first
        return latestDateB - latestDateA;
      });

      result.push({
        tournament: tournamentName,
        teams
      });
    });

    // Sort tournaments by the newest draft date in each tournament (newest tournament first)
    result.sort((a, b) => {
      // Get the newest date from all teams' drafts in each tournament
      const getNewestDate = (teams) => {
        let newest = new Date(0);
        teams.forEach(team => {
          if (team.drafts.length > 0) {
            const teamNewest = new Date(team.drafts[0].date || team.drafts[0].updatedAt || 0);
            if (teamNewest > newest) newest = teamNewest;
          }
        });
        return newest;
      };

      const latestDateA = getNewestDate(a.teams);
      const latestDateB = getNewestDate(b.teams);

      // Newest tournament first (descending)
      return latestDateB - latestDateA;
    });

    return result;
  }

  /**
   * Group drafts by team, then by tournament
   * @param {Array} drafts - Array of draft objects
   * @returns {Array} Grouped structure: [{ team, tournaments: [{ tournament, drafts: [...] }] }]
   */
  static groupByTeam(drafts) {
    if (!drafts || drafts.length === 0) return [];

    console.log('🎯 GROUP BY TEAM - Input drafts:', drafts.map(d => ({
      team: d.teamName,
      tournament: d.tournament,
      stage: d.stage,
      date: d.date
    })));

    // Create a map of teams -> tournaments -> drafts
    const teamMap = new Map();

    drafts.forEach(draft => {
      const teamName = draft.teamName || 'Unnamed Team';
      const tournamentName = draft.tournament || 'Uncategorized';

      console.log(`➕ Adding draft: team="${teamName}", tournament="${tournamentName}", stage="${draft.stage}", date="${draft.date}"`);

      // Get or create team entry
      if (!teamMap.has(teamName)) {
        teamMap.set(teamName, new Map());
      }

      const tournamentsMap = teamMap.get(teamName);

      // Get or create tournament entry
      if (!tournamentsMap.has(tournamentName)) {
        tournamentsMap.set(tournamentName, []);
        console.log(`  ✨ Created new tournament group: "${tournamentName}"`);
      }

      // Add draft to tournament
      tournamentsMap.get(tournamentName).push(draft);
      console.log(`  📦 Tournament "${tournamentName}" now has ${tournamentsMap.get(tournamentName).length} draft(s)`);
    });

    // Convert maps to arrays and sort
    const result = [];
    teamMap.forEach((tournamentsMap, teamName) => {
      const tournaments = [];
      tournamentsMap.forEach((drafts, tournamentName) => {
        tournaments.push({
          tournament: tournamentName,
          drafts: this.sortDraftsByDate(drafts)
        });
      });

      // Sort tournaments by the newest draft date in each tournament (newest tournament first)
      tournaments.sort((a, b) => {
        // Get the newest date from each tournament's drafts
        const latestDateA = a.drafts.length > 0 ? new Date(a.drafts[0].date || a.drafts[0].updatedAt || 0) : new Date(0);
        const latestDateB = b.drafts.length > 0 ? new Date(b.drafts[0].date || b.drafts[0].updatedAt || 0) : new Date(0);

        // Newest tournament first
        return latestDateB - latestDateA;
      });

      result.push({
        team: teamName,
        tournaments
      });
    });

    // Sort teams by the newest draft date in each team (newest team first)
    result.sort((a, b) => {
      // Get the newest date from all tournaments' drafts in each team
      const getNewestDate = (tournaments) => {
        let newest = new Date(0);
        tournaments.forEach(tournament => {
          if (tournament.drafts.length > 0) {
            const tournamentNewest = new Date(tournament.drafts[0].date || tournament.drafts[0].updatedAt || 0);
            if (tournamentNewest > newest) newest = tournamentNewest;
          }
        });
        return newest;
      };

      const latestDateA = getNewestDate(a.tournaments);
      const latestDateB = getNewestDate(b.tournaments);

      // Newest team first (descending)
      return latestDateB - latestDateA;
    });

    return result;
  }

  /**
   * Sort drafts by date (newest first)
   * @param {Array} drafts - Array of draft objects
   * @returns {Array} Sorted drafts
   */
  static sortDraftsByDate(drafts) {
    console.log('📥 BEFORE SORT:', drafts.map(d => ({ stage: d.stage, date: d.date })));

    const sorted = drafts.sort((a, b) => {
      const dateA = new Date(a.date || a.updatedAt || 0);
      const dateB = new Date(b.date || b.updatedAt || 0);

      const comparison = dateB.getTime() - dateA.getTime();

      console.log('🔍 COMPARING:', {
        a: { stage: a.stage, date: a.date, timestamp: dateA.getTime() },
        b: { stage: b.stage, date: b.date, timestamp: dateB.getTime() },
        comparison: comparison,
        result: comparison < 0 ? 'A first (keep order)' : comparison > 0 ? 'B first (swap)' : 'equal'
      });

      // Newest first (descending): larger timestamps come first
      // Return negative to keep a before b, positive to put b before a
      return comparison;
    });

    console.log('📤 AFTER SORT:', sorted.map(d => ({ stage: d.stage, date: d.date })));

    return sorted;
  }

  /**
   * Get summary statistics for drafts
   * @param {Array} drafts - Array of draft objects
   * @returns {Object} Statistics object
   */
  static getStatistics(drafts) {
    if (!drafts || drafts.length === 0) {
      return {
        totalDrafts: 0,
        totalTournaments: 0,
        totalTeams: 0
      };
    }

    const tournaments = new Set();
    const teams = new Set();

    drafts.forEach(draft => {
      if (draft.tournament) tournaments.add(draft.tournament);
      if (draft.teamName) teams.add(draft.teamName);
    });

    return {
      totalDrafts: drafts.length,
      totalTournaments: tournaments.size,
      totalTeams: teams.size
    };
  }

  /**
   * Search/filter drafts by keyword
   * @param {Array} drafts - Array of draft objects
   * @param {String} keyword - Search keyword
   * @returns {Array} Filtered drafts
   */
  static searchDrafts(drafts, keyword) {
    if (!keyword || keyword.trim() === '') return drafts;

    const lowerKeyword = keyword.toLowerCase().trim();

    return drafts.filter(draft => {
      const teamName = (draft.teamName || '').toLowerCase();
      const tournament = (draft.tournament || '').toLowerCase();
      const stage = (draft.stage || '').toLowerCase();
      const date = (draft.date || '').toLowerCase();

      return (
        teamName.includes(lowerKeyword) ||
        tournament.includes(lowerKeyword) ||
        stage.includes(lowerKeyword) ||
        date.includes(lowerKeyword)
      );
    });
  }

  /**
   * Format draft display title
   * @param {Object} draft - Draft object
   * @returns {String} Formatted title
   */
  static formatDraftTitle(draft) {
    const parts = [];

    if (draft.teamName) parts.push(draft.teamName);
    if (draft.tournament) parts.push(draft.tournament);
    if (draft.stage) parts.push(draft.stage);
    if (draft.date) parts.push(draft.date);

    return parts.length > 0 ? parts.join(' - ') : 'Untitled Draft';
  }
}

export default RecordViewerService;
